// AppErrorBoundary — red de seguridad de TODA la app.
// Sin él, cualquier error de render desmonta el árbol entero y el usuario ve una pantalla
// gris vacía sin salida (incidente Poli's barber shop, 10/9/2026: el traductor de Chrome iOS
// reescribía el DOM y React caía con "removeChild … is not a child of this node").
// Con él: pantalla de "Algo ha fallado" con botón de recargar + aviso a Telegram vía
// POST /client-error (c_reportClientError → console.error → c_errorMonitor).
//
// COPIADO POR VALOR (mismo fichero, mismo md5) en cada app de apps/*/src/components/:
// los paquetes compartidos llevan el dist commiteado y el deploy no los recompila.
// Lo que cambia por app va en las props `app` y `tokenKey`, nunca en el fichero.
import React from "react";

// Un error en local es alguien programando, no una incidencia: los reportes de
// localhost enterraban las alertas reales de Telegram (17/9/2026, dos «ERROR NUEVO»
// de food-orders que eran un JSX a medio escribir en el portátil).
function isLocalDev() {
  try {
    const h = window.location.hostname;
    return (
      h === "localhost" ||
      h === "127.0.0.1" ||
      h === "[::1]" ||
      h === "0.0.0.0" ||
      h.endsWith(".local") ||
      /^(10|127)\./.test(h) ||
      /^192\.168\./.test(h) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(h)
    );
  } catch (_) {
    return false;
  }
}

function reportClientError({ app, tokenKey, error, componentStack }) {
  try {
    const token = localStorage.getItem(tokenKey);
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    // Sin sesión no hay a quién atribuirlo y el endpoint exige JWT: no se reporta.
    if (!token || !baseUrl) return;
    // En local se ve en la consola del navegador; no se manda a Telegram.
    if (isLocalDev()) return;
    // fetch y no axiosClient: sus interceptores pueden abrir el modal de sesión caducada
    // o redirigir, y aquí la app ya está rota. Si falla, se ignora en silencio.
    fetch(`${baseUrl}/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        app,
        url: window.location.href,
        message: String(error?.message || error),
        stack: error?.stack || "",
        component_stack: componentStack || "",
        user_agent: navigator.userAgent,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    // localStorage bloqueado, etc.: el reporte nunca debe romper el fallback.
  }
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("[AppErrorBoundary]", error, info?.componentStack);
    reportClientError({
      app: this.props.app,
      tokenKey: this.props.tokenKey || "token",
      error,
      componentStack: info?.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Estilos inline y texto fijo en español: el fallback no puede depender de Tailwind,
    // i18n ni ningún provider — cualquiera de ellos puede ser lo que se ha roto.
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f3f4f6",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "360px",
            width: "100%",
            background: "#fff",
            borderRadius: "16px",
            padding: "28px 24px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <h1 style={{ fontSize: "18px", fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
            Algo ha fallado
          </h1>
          <p style={{ fontSize: "14px", color: "#6b7280", margin: "0 0 20px", lineHeight: 1.5 }}>
            La página no se ha podido mostrar. Recarga para seguir trabajando y, si vuelve a
            pasar, avísanos.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: "15px",
              fontWeight: 600,
              color: "#fff",
              background: "#2563eb",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
            }}
          >
            Recargar
          </button>
        </div>
      </div>
    );
  }
}
